import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useAuth } from '@/auth/hooks/useAuth';

export interface ArcheBadge {
  count: number;
  mentionCount: number;
}

/**
 * Cross-arche notification badge summary for the header + worktop.
 *
 * Polls every ~45s while the tab is visible (paused when hidden) and refetches
 * on window focus (global default). Local actions that change counts — e.g.
 * reading a chat — should invalidate `queryKeys.notifications.summary()` to
 * update immediately instead of waiting for the poll.
 *
 * Disabled when logged out, so the header (which renders for unauthenticated
 * users) never fires the request.
 */
export function useNotificationSummary() {
  const { isAuthenticated } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.notifications.summary(),
    queryFn: () => api.getNotificationSummary(),
    enabled: isAuthenticated,
    refetchInterval: 45_000,
    refetchIntervalInBackground: false,
    staleTime: 30_000,
  });

  const badges = useMemo(() => {
    const map: Record<string, ArcheBadge> = {};
    for (const b of data?.badges ?? []) {
      map[b.archeId] = { count: b.count, mentionCount: b.mentionCount };
    }
    return map;
  }, [data]);

  return {
    /** Per-arche badge lookup keyed by archeId. */
    badges,
    total: data?.total ?? 0,
    totalMentions: data?.totalMentions ?? 0,
    isLoading,
  };
}
